import React, { createContext, useContext, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { TechnicianUser } from '../types';

interface AuthContextValue {
  user: TechnicianUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sendOtp: (mobile: string) => Promise<void>;
  verifyOtp: (mobile: string, otp: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getStoredUser = (): TechnicianUser | null => {
  const token = localStorage.getItem('technicianToken');
  const userJson = localStorage.getItem('technicianUser');

  if (!token || !userJson) {
    return null;
  }

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
};

const isTechnician = (user: TechnicianUser): boolean =>
  (user.roles || []).some((role) => role.toLowerCase() === 'technician');

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storedUser = getStoredUser();
  const [user, setUser] = useState<TechnicianUser | null>(storedUser);
  const [isLoading] = useState(false);

  const sendOtp = async (mobile: string) => {
    const response = await apiService.sendOtp(mobile);
    if (!response.isSuccess) {
      throw new Error(response.errors?.join(', ') || response.message || 'Failed to send OTP');
    }
  };

  const verifyOtp = async (mobile: string, otp: string) => {
    const response = await apiService.verifyOtp(mobile, otp);
    if (!isTechnician(response.user)) {
      throw new Error('Only technician users can access this app');
    }

    localStorage.setItem('technicianToken', response.token);
    localStorage.setItem('technicianTokenExpiresAt', response.expiresAt);
    localStorage.setItem('technicianUser', JSON.stringify(response.user));
    setUser(response.user);
  };

  const logout = () => {
    localStorage.removeItem('technicianToken');
    localStorage.removeItem('technicianTokenExpiresAt');
    localStorage.removeItem('technicianUser');
    setUser(null);
  };

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    sendOtp,
    verifyOtp,
    logout,
  }), [user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
